import { useState, useEffect, useCallback } from "react";
import { UserPlus, Check, X as XIcon, Clock, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";

interface PendingRequest {
  id: string;
  user_id: string;
  created_at: string;
  display_name: string;
  avatar_url: string;
}

interface OutgoingRequest {
  id: string;
  friend_id: string;
  created_at: string;
  display_name: string;
  avatar_url: string;
}

const FriendRequestBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [outgoing, setOutgoing] = useState<OutgoingRequest[]>([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"incoming" | "outgoing">("incoming");
  const [showPopup, setShowPopup] = useState(false);
  const [latestRequest, setLatestRequest] = useState<PendingRequest | null>(null);
  const [seenIds, setSeenIds] = useState<Set<string>>(() => {
    const stored = sessionStorage.getItem("seen_friend_requests");
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  const loadRequests = useCallback(async () => {
    if (!user) return;

    // Load incoming
    const { data: friendships } = await supabase
      .from("friends")
      .select("id, user_id, created_at")
      .eq("friend_id", user.id)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    // Load outgoing
    const { data: outgoingData } = await supabase
      .from("friends")
      .select("id, friend_id, created_at")
      .eq("user_id", user.id)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    // Enrich incoming
    const senderIds = (friendships || []).map((f) => f.user_id);
    const recipientIds = (outgoingData || []).map((f) => f.friend_id);
    const allUserIds = [...new Set([...senderIds, ...recipientIds])];

    let profileMap = new Map<string, { display_name: string; avatar_url: string }>();
    if (allUserIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url")
        .in("user_id", allUserIds);
      profileMap = new Map((profiles || []).map((p) => [p.user_id, p]));
    }

    const enrichedIncoming: PendingRequest[] = (friendships || []).map((f) => {
      const profile = profileMap.get(f.user_id);
      return {
        id: f.id,
        user_id: f.user_id,
        created_at: f.created_at,
        display_name: profile?.display_name || "Someone",
        avatar_url: profile?.avatar_url || "",
      };
    });

    const enrichedOutgoing: OutgoingRequest[] = (outgoingData || []).map((f) => {
      const profile = profileMap.get(f.friend_id);
      return {
        id: f.id,
        friend_id: f.friend_id,
        created_at: f.created_at,
        display_name: profile?.display_name || "Someone",
        avatar_url: profile?.avatar_url || "",
      };
    });

    setRequests(enrichedIncoming);
    setOutgoing(enrichedOutgoing);

    // Check for new unseen requests to show popup
    const newUnseen = enrichedIncoming.filter((r) => !seenIds.has(r.id));
    if (newUnseen.length > 0) {
      setLatestRequest(newUnseen[0]);
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 6000);
      const updatedSeen = new Set(seenIds);
      newUnseen.forEach((r) => updatedSeen.add(r.id));
      setSeenIds(updatedSeen);
      sessionStorage.setItem(
        "seen_friend_requests",
        JSON.stringify([...updatedSeen])
      );
    }
  }, [user, seenIds]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("friend-requests")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "friends", filter: `friend_id=eq.${user.id}` },
        () => loadRequests()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "friends", filter: `user_id=eq.${user.id}` },
        () => loadRequests()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, loadRequests]);

  const handleAccept = async (requestId: string, senderName: string) => {
    await supabase
      .from("friends")
      .update({ status: "accepted" } as any)
      .eq("id", requestId);

    setRequests((prev) => prev.filter((r) => r.id !== requestId));
    toast({
      title: "Friend added! 🎉",
      description: `You and ${senderName} are now friends.`,
    });
  };

  const handleDecline = async (requestId: string) => {
    await supabase.from("friends").delete().eq("id", requestId);
    setRequests((prev) => prev.filter((r) => r.id !== requestId));
  };

  const handleCancelOutgoing = async (requestId: string, recipientName: string) => {
    await supabase.from("friends").delete().eq("id", requestId);
    setOutgoing((prev) => prev.filter((r) => r.id !== requestId));
    toast({ title: "Request cancelled", description: `Friend request to ${recipientName} was cancelled.` });
  };

  if (!user) return null;

  const totalCount = requests.length + outgoing.length;

  return (
    <>
      {/* Floating pop-up notification */}
      <AnimatePresence>
        {showPopup && latestRequest && (
          <motion.div
            initial={{ opacity: 0, y: -40, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -40, x: "-50%" }}
            className="fixed top-20 left-1/2 z-[100] w-[90vw] max-w-sm"
          >
            <div className="bg-card border border-border rounded-2xl shadow-2xl p-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 border-2 border-primary/30">
                  <AvatarImage src={latestRequest.avatar_url} />
                  <AvatarFallback className="bg-primary/10 text-primary font-display text-sm">
                    {latestRequest.display_name?.[0]?.toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-display font-semibold text-foreground truncate">
                    {latestRequest.display_name}
                  </p>
                  <p className="text-xs text-muted-foreground">sent you a friend request</p>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    className="h-8 rounded-full gradient-hero border-0 text-xs font-display"
                    onClick={() => {
                      handleAccept(latestRequest.id, latestRequest.display_name);
                      setShowPopup(false);
                    }}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" />
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 rounded-full"
                    onClick={() => setShowPopup(false)}
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bell icon in navbar */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <UserPlus className="h-4 w-4" />
            {totalCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center animate-pulse">
                {totalCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          {/* Tabs */}
          <div className="flex border-b border-border">
            <button
              onClick={() => setTab("incoming")}
              className={`flex-1 px-4 py-2.5 text-xs font-display font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                tab === "incoming"
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <ArrowDownLeft className="h-3.5 w-3.5" />
              Incoming{requests.length > 0 && ` (${requests.length})`}
            </button>
            <button
              onClick={() => setTab("outgoing")}
              className={`flex-1 px-4 py-2.5 text-xs font-display font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                tab === "outgoing"
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
              Sent{outgoing.length > 0 && ` (${outgoing.length})`}
            </button>
          </div>

          <ScrollArea className="max-h-[360px]">
            {tab === "incoming" ? (
              requests.length === 0 ? (
                <div className="py-8 text-center">
                  <UserPlus className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No pending requests</p>
                </div>
              ) : (
                requests.map((req) => (
                  <div key={req.id} className="px-4 py-3 border-b border-border/50 flex items-center gap-3">
                    <button onClick={() => { setOpen(false); navigate(`/profile/${req.user_id}`); }} className="shrink-0">
                      <Avatar className="h-10 w-10 border-2 border-border hover:border-primary/50 transition-colors">
                        <AvatarImage src={req.avatar_url} />
                        <AvatarFallback className="bg-secondary text-foreground font-display text-sm">
                          {req.display_name?.[0]?.toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => { setOpen(false); navigate(`/profile/${req.user_id}`); }}
                        className="text-sm font-display font-semibold text-foreground truncate block text-left hover:text-primary transition-colors"
                      >
                        {req.display_name}
                      </button>
                      <p className="text-[11px] text-muted-foreground">
                        {formatDistanceToNow(new Date(req.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Button size="sm" className="h-7 px-2.5 rounded-full gradient-hero border-0 text-xs font-display" onClick={() => handleAccept(req.id, req.display_name)}>
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 rounded-full text-muted-foreground hover:text-destructive" onClick={() => handleDecline(req.id)}>
                        <XIcon className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )
            ) : (
              outgoing.length === 0 ? (
                <div className="py-8 text-center">
                  <Clock className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No sent requests</p>
                </div>
              ) : (
                outgoing.map((req) => (
                  <div key={req.id} className="px-4 py-3 border-b border-border/50 flex items-center gap-3">
                    <button onClick={() => { setOpen(false); navigate(`/profile/${req.friend_id}`); }} className="shrink-0">
                      <Avatar className="h-10 w-10 border-2 border-border hover:border-primary/50 transition-colors">
                        <AvatarImage src={req.avatar_url} />
                        <AvatarFallback className="bg-secondary text-foreground font-display text-sm">
                          {req.display_name?.[0]?.toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => { setOpen(false); navigate(`/profile/${req.friend_id}`); }}
                        className="text-sm font-display font-semibold text-foreground truncate block text-left hover:text-primary transition-colors"
                      >
                        {req.display_name}
                      </button>
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Pending · {formatDistanceToNow(new Date(req.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2.5 rounded-full text-xs font-display text-muted-foreground hover:text-destructive"
                      onClick={() => handleCancelOutgoing(req.id, req.display_name)}
                    >
                      Cancel
                    </Button>
                  </div>
                ))
              )
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default FriendRequestBell;
